import { PagedQuery } from "@rsm-hcd/javascript-core";
import type UserRoleGroupRecord from "models/view-models/user-role-group-record";
import usePageErrors from "utilities/hooks/use-page-errors";
import ServiceHookResult from "utilities/interfaces/service-hook-result";
import NumberUtils from "utilities/number-utils";
import UserRoleGroupService from "utilities/services/user-role-groups/user-role-group-service";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface UseUserRoleGroupsOptions extends PagedQuery {
    groupId?: string | number;
    includeUserRoleAndUser?: boolean;
    searchText?: string;
}

interface UseUserRoleGroupsResult
    extends ServiceHookResult<UserRoleGroupRecord[]> {
    /**
     * Function for the consumer to call to trigger a refresh of the data gathered by this hook
     */
    refresh: () => void;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const defaultOptionValues: UseUserRoleGroupsOptions = {
    groupId: undefined,
    includeUserRoleAndUser: false,
    searchText: undefined,
    skip: undefined,
    take: undefined,
};

// #endregion Constants
/**
 * Custom hook for retrieving UserRoleGroups, with the option to include nested data.
 *
 * @param groupId Id of the Group to retrieve UserRoleGroups by.
 * @param includeUserRoleAndUser Flag to determine whether or not UserRoles and Users should be
 * included on the UserRoleGroupRecords.
 */
export default function useUserRoleGroups(
    options: UseUserRoleGroupsOptions
): UseUserRoleGroupsResult {
    const { groupId, includeUserRoleAndUser, searchText, skip, take } =
        Object.assign({}, defaultOptionValues, options);
    const { handlePageLoadError, pageErrors: errors } = usePageErrors();

    const { list: listUserRoleGroupsQuery } =
        UserRoleGroupService.useListQuery();

    const {
        data: userRoleGroupsResult,
        isLoading,
        isFetched,
        isFetching,
        refetch,
    } = listUserRoleGroupsQuery(
        {
            groupId: NumberUtils.parseInt(groupId),
            searchText: searchText,
            skip: skip,
            take: take,
            includeNavProperties: includeUserRoleAndUser,
        },
        {
            enabled:
                groupId != null &&
                typeof NumberUtils.parseInt(groupId) === "number",
            onError: (error) => handlePageLoadError(error),
            staleTime: 5 * 60 * 1000, // 5 minutes
        }
    );

    return {
        errors,
        loaded: isFetched,
        loading: isLoading || isFetching,
        refresh: refetch,
        resultObject: userRoleGroupsResult?.resultObjects ?? [],
        rowCount: userRoleGroupsResult?.rowCount ?? 0,
    };
}
